import { sbEditable } from '@storyblok/storyblok-editable';
import React from 'react';

enum ContentSpacingSize {
  XS = 'XS',
  SM = 'SM',
  MD = 'MD',
  LG = 'LG',
  XL = 'XL',
}

enum ContentSpacingBackgroundColor {
  PURPLE = 'PURPLE',
  GREEN = 'GREEN',
  BLACK = 'BLACK',
  WHITE = 'WHITE',
  GREY = 'GREY',
  DEFAULT = 'DEFAULT',
}

enum ScreenSizeMultiplicationFactor {
  ONE = 'ONE',
  TWO = 'TWO',
  TWOHALF = 'TWOHALF',
}

type HeightMap = {
  [key in `${ContentSpacingSize}_${ScreenSizeMultiplicationFactor}`]: string;
};

export interface ContentSpacerProps {
  spacing: ContentSpacingSize;
  multiplicationFactor?: ScreenSizeMultiplicationFactor;
  bgColor: ContentSpacingBackgroundColor;
  component: string;
  _uid: string;
  _editable?: string;
}

const ContentSpacer: React.FC<ContentSpacerProps> = (props) => {
  const {
    spacing,
    multiplicationFactor,
    bgColor = ContentSpacingBackgroundColor.DEFAULT,
  } = props;

  const background =
    bgColor in ContentSpacingBackgroundColor
      ? bgColor
      : ContentSpacingBackgroundColor.DEFAULT;
  const factor =
    multiplicationFactor &&
    multiplicationFactor in ScreenSizeMultiplicationFactor
      ? ScreenSizeMultiplicationFactor[multiplicationFactor]
      : ScreenSizeMultiplicationFactor.ONE;
  const space = spacing in ContentSpacingSize ? spacing : ContentSpacingSize.MD;

  const classMap: HeightMap = {
    XS_ONE: 'h-5px',
    SM_ONE: 'h-10px',
    MD_ONE: 'h-40px',
    LG_ONE: 'h-80px',
    XL_ONE: 'h-100px',
    XS_TWO: 'h-[2.5px] lg:h-5px',
    SM_TWO: 'h-5px lg:h-10px',
    MD_TWO: 'h-20px lg:h-40px',
    LG_TWO: 'h-40px lg:h-80px',
    XL_TWO: 'h-[50px] lg:h-100px',
    XS_TWOHALF: 'h-[2px] lg:h-5px',
    SM_TWOHALF: 'h-[4px] lg:h-10px',
    MD_TWOHALF: 'h-[16px] lg:h-40px',
    LG_TWOHALF: 'h-[32px] lg:h-80px',
    XL_TWOHALF: 'h-40px lg:h-100px',
  };

  let bg = '';
  switch (background) {
    case ContentSpacingBackgroundColor.PURPLE:
      bg = 'bg-purple80';
      break;
    case ContentSpacingBackgroundColor.GREEN:
      bg = 'bg-green60';
      break;
    case ContentSpacingBackgroundColor.BLACK:
      bg = 'bg-black';
      break;
    case ContentSpacingBackgroundColor.WHITE:
      bg = 'bg-white';
      break;
    case ContentSpacingBackgroundColor.GREY:
      bg = 'bg-grey10';
      break;
    case ContentSpacingBackgroundColor.DEFAULT:
      bg = 'bg-transparent';
      break;
  }

  const heightClassName = classMap[`${space}_${factor}`];
  const classNames = {
    spacer: `w-full ${bg} ${heightClassName}`,
  };

  return <div {...sbEditable(props)} className={classNames.spacer} />;
};

export default ContentSpacer;
